import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Trash2, CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp, Loader2, AlertTriangle } from 'lucide-react';
import { masterChangeAPI, fetchPaged } from '../../services/api';
import Pagination from '../ui/Pagination';
import { toast } from 'sonner';

const ENTITY_LABELS = { customer: 'Customer', product: 'Product', bom: 'BOM', branch: 'Branch', vendor: 'Vendor' };
const ACTION_COLORS = { edit: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' }, delete: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' } };
const STATUS_COLORS = { Pending: 'bg-amber-100 text-amber-800', Approved: 'bg-green-100 text-green-800', Rejected: 'bg-red-100 text-red-800' };

const MasterChangesTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Pending');
  const [expandedId, setExpandedId] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');
  const isAdmin = user.role === 'Admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (filter) params.status_filter = filter;
      const [result, st] = await Promise.all([
        fetchPaged('/master-changes', params),
        masterChangeAPI.getStats(),
      ]);
      setRequests(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(st);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    try {
      setProcessing(id);
      await masterChangeAPI.approve(id);
      toast.success('Request approved successfully');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
    finally { setProcessing(null); }
  };

  const handleReject = async () => {
    if (!rejectId) return;
    try {
      setProcessing(rejectId);
      await masterChangeAPI.reject(rejectId, rejectReason);
      toast.success('Request rejected');
      setRejectId(null); setRejectReason('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
    finally { setProcessing(null); }
  };

  const renderChanges = (req) => {
    if (req.action === 'delete') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
          <p className="text-xs font-semibold text-red-700 mb-1">Requesting soft-delete (deactivate)</p>
          <p className="text-[11px] text-red-600">This {ENTITY_LABELS[req.entity_type]} will be hidden from all active lists.</p>
          {req.reason && <p className="text-[11px] text-gray-600 mt-1">Reason: {req.reason}</p>}
        </div>
      );
    }
    if (!req.changes || !req.original_data) return null;
    const changedFields = Object.keys(req.changes).filter(k => {
      if (['id', '_id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'is_active'].includes(k)) return false;
      return JSON.stringify(req.changes[k]) !== JSON.stringify(req.original_data[k]);
    });
    if (changedFields.length === 0) return <p className="text-[11px] text-gray-400 mt-2">No field changes detected.</p>;
    return (
      <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-[11px]">
          <thead><tr className="bg-gray-50"><th className="px-3 py-1.5 text-left text-gray-500 font-medium">Field</th><th className="px-3 py-1.5 text-left text-gray-500 font-medium">Current</th><th className="px-3 py-1.5 text-left text-gray-500 font-medium">Proposed</th></tr></thead>
          <tbody>
            {changedFields.map(field => (
              <tr key={field} className="border-t border-gray-100">
                <td className="px-3 py-1.5 font-medium text-gray-700 capitalize">{field.replace(/_/g, ' ')}</td>
                <td className="px-3 py-1.5 text-red-600 line-through">{typeof req.original_data[field] === 'object' ? JSON.stringify(req.original_data[field]) : String(req.original_data[field] ?? '-')}</td>
                <td className="px-3 py-1.5 text-green-700 font-medium">{typeof req.changes[field] === 'object' ? JSON.stringify(req.changes[field]) : String(req.changes[field] ?? '-')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {req.reason && <p className="px-3 py-1.5 text-[11px] text-gray-500 border-t border-gray-100">Reason: {req.reason}</p>}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        {[
          { key: 'Pending', label: 'Pending', count: stats.pending, color: '#E65100' },
          { key: 'Approved', label: 'Approved', count: stats.approved, color: '#2E7D32' },
          { key: 'Rejected', label: 'Rejected', count: stats.rejected, color: '#C62828' },
          { key: '', label: 'All', count: stats.pending + stats.approved + stats.rejected, color: '#24549c' },
        ].map(f => (
          <button key={f.key} onClick={() => { setFilter(f.key); setPage(1); }} data-testid={`mc-filter-${f.key || 'all'}`}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all ${filter === f.key ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
            style={filter === f.key ? { backgroundColor: f.color } : {}}>
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">No {filter.toLowerCase() || ''} change requests</div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => {
            const isExpanded = expandedId === req.id;
            const ac = ACTION_COLORS[req.action] || ACTION_COLORS.edit;
            return (
              <div key={req.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`mc-request-${req.id}`}>
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-lg ${ac.bg}`}>
                      {req.action === 'edit' ? <Edit2 className={`w-3.5 h-3.5 ${ac.text}`} /> : <Trash2 className={`w-3.5 h-3.5 ${ac.text}`} />}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{req.action === 'edit' ? 'Edit' : 'Delete'} {ENTITY_LABELS[req.entity_type]}: <span className="text-blue-700">{req.entity_name}</span></p>
                      <p className="text-[10px] text-gray-400">by {req.requested_by_name} · {new Date(req.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${STATUS_COLORS[req.status]}`}>{req.status}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-gray-100 pt-2">
                    {renderChanges(req)}
                    {req.status === 'Pending' && isAdmin && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => handleApprove(req.id)} disabled={processing === req.id} data-testid={`mc-approve-${req.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                          {processing === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Approve
                        </button>
                        <button onClick={() => setRejectId(req.id)} disabled={processing === req.id} data-testid={`mc-reject-${req.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    )}
                    {req.status === 'Rejected' && req.rejection_reason && (
                      <p className="mt-2 text-[11px] text-red-600">Rejection reason: {req.rejection_reason}</p>
                    )}
                    {req.reviewed_by_name && (
                      <p className="mt-1 text-[10px] text-gray-400">Reviewed by {req.reviewed_by_name}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {requests.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setRejectId(null)}>
          <div className="bg-white rounded-xl p-5 w-96 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h3 className="text-sm font-bold text-gray-900">Reject Change Request</h3>
            </div>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." rows={3}
              className="w-full text-xs border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:ring-1 focus:ring-red-200" data-testid="mc-reject-reason" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setRejectId(null); setRejectReason(''); }} className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleReject} disabled={processing} data-testid="mc-reject-confirm"
                className="px-3 py-1.5 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterChangesTab;
